import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { FileText, CalendarClock, MessageSquare, TrendingUp, ArrowUpRight } from "lucide-react";

const currency = (n) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n);

export default function Dashboard({ onNavigate }) {
  const { user } = useAuth();
  const [s, setS] = useState(null);

  useEffect(() => {
    api.get("/portal/summary").then(({ data }) => setS(data)).catch(() => {});
  }, []);

  const cards = [
    { key: "portfolio", label: "Portfolio Value", value: s ? currency(s.portfolio_value) : "—", icon: TrendingUp, tone: "navy" },
    { key: "documents", label: "Documents", value: s?.documents ?? "—", icon: FileText, action: "documents" },
    { key: "appointments", label: "Upcoming Meetings", value: s?.appointments ?? "—", icon: CalendarClock, action: "appointments" },
    { key: "messages", label: "Unread Messages", value: s?.unread_messages ?? "—", icon: MessageSquare, action: "messages" },
  ];

  return (
    <div data-testid="portal-dashboard">
      <div className="mb-8">
        <p className="overline text-primary">Client Portal</p>
        <h1 className="font-serif text-4xl text-slate-900 mt-1">Welcome back, {user?.name?.split(" ")[0] || "Client"}.</h1>
        <p className="text-slate-500 mt-2">Here&apos;s a snapshot of your financial relationship with Sagos.</p>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map((c, i) => (
          <motion.div
            key={c.key}
            initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.08, duration: 0.5 }}
            data-testid={`stat-${c.key}`}
            onClick={() => c.action && onNavigate?.(c.action)}
            className={`rounded-2xl border p-6 ${c.tone === "navy" ? "bg-[#0b2a5b] border-[#0b2a5b] text-white" : "bg-white border-border"} ${c.action ? "cursor-pointer hover:border-primary transition-colors duration-200" : ""}`}
          >
            <div className="flex items-center justify-between">
              <div className={`h-10 w-10 rounded-xl flex items-center justify-center ${c.tone === "navy" ? "bg-white/10 text-[#8fb4f5]" : "bg-accent text-primary"}`}>
                <c.icon size={18} />
              </div>
              {c.action && <ArrowUpRight size={16} className={c.tone === "navy" ? "text-white/50" : "text-slate-300"} />}
            </div>
            <p className={`mt-5 font-serif text-3xl ${c.tone === "navy" ? "text-white" : "text-slate-900"}`}>{c.value}</p>
            <p className={`overline mt-2 ${c.tone === "navy" ? "text-white/60" : "text-slate-400"}`}>{c.label}</p>
          </motion.div>
        ))}
      </div>

      <div className="grid lg:grid-cols-3 gap-4 mt-4">
        <div className="lg:col-span-2 rounded-2xl border border-border bg-white p-7">
          <div className="flex items-center justify-between">
            <h3 className="font-serif text-2xl text-slate-900">Performance</h3>
            {s && <span className="text-sm font-medium text-emerald-600">+{s.ytd_return}% YTD</span>}
          </div>
          <div className="mt-6 flex items-end gap-2 h-40">
            {[42, 55, 48, 63, 58, 72, 68, 81, 76, 88, 84, 95].map((h, i) => (
              <div key={i} className="flex-1 rounded-t bg-gradient-to-t from-primary/20 to-primary" style={{ height: `${h}%` }} />
            ))}
          </div>
          <div className="mt-3 flex justify-between overline text-slate-400">
            <span>Jan</span><span>Jun</span><span>Dec</span>
          </div>
        </div>

        <div className="rounded-2xl border border-border bg-white p-7">
          <h3 className="font-serif text-2xl text-slate-900">Next Meeting</h3>
          {s?.next_appointment ? (
            <div className="mt-5">
              <p className="font-serif text-3xl text-primary">{s.next_appointment.date}</p>
              <p className="text-slate-600 mt-2">{s.next_appointment.time}</p>
              <p className="mt-4 text-slate-800 font-medium">{s.next_appointment.topic}</p>
              <button onClick={() => onNavigate?.("appointments")} className="mt-6 text-sm font-medium text-primary hover:underline" data-testid="dashboard-view-appointments">
                Manage appointments →
              </button>
            </div>
          ) : (
            <div className="mt-5">
              <p className="text-slate-500">No upcoming meetings scheduled.</p>
              <button onClick={() => onNavigate?.("appointments")} className="mt-5 px-5 py-2.5 rounded-full bg-primary text-primary-foreground text-sm font-medium hover:bg-[#0b2a5b] transition-colors">
                Book a consultation
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
